// Feature translation bundles. Keep every supported language available in each bundle.

export const TRANSLATION_REGISTRY = {
  components: {
    en: () => import("../components/_i18n/en.json"),
    jp: () => import("../components/_i18n/jp.json"),
    pt: () => import("../components/_i18n/pt.json"),
    es: () => import("../components/_i18n/es.json"),
  },
  "components/community": {
    en: () => import("../components/community/_i18n/en.json"),
    jp: () => import("../components/community/_i18n/jp.json"),
    pt: () => import("../components/community/_i18n/pt.json"),
    es: () => import("../components/community/_i18n/es.json"),
  },
  "components/home": {
    en: () => import("../components/home/_i18n/en.json"),
    jp: () => import("../components/home/_i18n/jp.json"),
    pt: () => import("../components/home/_i18n/pt.json"),
    es: () => import("../components/home/_i18n/es.json"),
  },
  "components/login": {
    en: () => import("../components/login/_i18n/en.json"),
    jp: () => import("../components/login/_i18n/jp.json"),
    pt: () => import("../components/login/_i18n/pt.json"),
    es: () => import("../components/login/_i18n/es.json"),
  },
  "components/partners": {
    en: () => import("../components/partners/_i18n/en.json"),
    jp: () => import("../components/partners/_i18n/jp.json"),
    pt: () => import("../components/partners/_i18n/pt.json"),
    es: () => import("../components/partners/_i18n/es.json"),
  },
  "components/protectedWords": {
    en: () => import("../components/protectedWords/_i18n/en.json"),
    jp: () => import("../components/protectedWords/_i18n/jp.json"),
    pt: () => import("../components/protectedWords/_i18n/pt.json"),
    es: () => import("../components/protectedWords/_i18n/es.json"),
  },
  "components/search": {
    en: () => import("../components/search/_i18n/en.json"),
    jp: () => import("../components/search/_i18n/jp.json"),
    pt: () => import("../components/search/_i18n/pt.json"),
    es: () => import("../components/search/_i18n/es.json"),
  },
  "components/session": {
    en: () => import("../components/session/_i18n/en.json"),
    jp: () => import("../components/session/_i18n/jp.json"),
    pt: () => import("../components/session/_i18n/pt.json"),
    es: () => import("../components/session/_i18n/es.json"),
  },
  "components/tour": {
    en: () => import("../components/tour/_i18n/en.json"),
    jp: () => import("../components/tour/_i18n/jp.json"),
    pt: () => import("../components/tour/_i18n/pt.json"),
    es: () => import("../components/tour/_i18n/es.json"),
  },
  "components/tournament": {
    en: () => import("../components/tournament/_i18n/en.json"),
    jp: () => import("../components/tournament/_i18n/jp.json"),
    pt: () => import("../components/tournament/_i18n/pt.json"),
    es: () => import("../components/tournament/_i18n/es.json"),
  },
  "components/ui": {
    en: () => import("../components/ui/_i18n/en.json"),
    jp: () => import("../components/ui/_i18n/jp.json"),
    pt: () => import("../components/ui/_i18n/pt.json"),
    es: () => import("../components/ui/_i18n/es.json"),
  },
  "components/upload": {
    en: () => import("../components/upload/_i18n/en.json"),
    jp: () => import("../components/upload/_i18n/jp.json"),
    pt: () => import("../components/upload/_i18n/pt.json"),
    es: () => import("../components/upload/_i18n/es.json"),
  },
  "components/users": {
    en: () => import("../components/users/_i18n/en.json"),
    jp: () => import("../components/users/_i18n/jp.json"),
    pt: () => import("../components/users/_i18n/pt.json"),
    es: () => import("../components/users/_i18n/es.json"),
  },
  "components/verify": {
    en: () => import("../components/verify/_i18n/en.json"),
    jp: () => import("../components/verify/_i18n/jp.json"),
    pt: () => import("../components/verify/_i18n/pt.json"),
    es: () => import("../components/verify/_i18n/es.json"),
  },
  views: {
    en: () => import("../views/_i18n/en.json"),
    jp: () => import("../views/_i18n/jp.json"),
    pt: () => import("../views/_i18n/pt.json"),
    es: () => import("../views/_i18n/es.json"),
  },
} as const;

export type ComponentPath = keyof typeof TRANSLATION_REGISTRY;

/**
 * Supported languages across all components
 * Auto-detected from _i18n/*.json files
 */
export type Language = "en" | "jp" | "pt" | "es";

/**
 * List of all supported languages
 */
export const SUPPORTED_LANGUAGES: Language[] = ["en", "jp", "pt", "es"];

/**
 * Accepted languages for the app
 * Add new languages here to enable them
 */
/**
 * Map an internal language code to a BCP 47 Intl locale string.
 * Add entries here when new languages are enabled.
 */
export const LANG_TO_LOCALE: Record<string, string> = {
  jp: "ja-JP",
  en: "en-US",
  es: "es-ES",
  pt: "pt-BR",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  ko: "ko-KR",
  zh: "zh-CN",
  "zh-TW": "zh-TW",
  ru: "ru-RU",
  ar: "ar-SA",
  hi: "hi-IN",
};

export const langToLocale = (lang: string): string =>
  LANG_TO_LOCALE[lang] ?? "en-US";

export const ACCEPTED_LANGUAGES: string[] = [
  "en",
  "es",
  "pt",
  "fr",
  "de",
  "it",
  "jp",
  "ko",
  "zh",
  "zh-TW",
  "ru",
  "ar",
  "hi",
];

/**
 * Language display names
 */
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: "English",
  jp: "日本語",
  pt: "Português (Brasil)",
  es: "Español",
};

/**
 * Language flag emojis
 */
export const LANGUAGE_FLAGS: Record<Language, string> = {
  en: "🇺🇸",
  jp: "🇯🇵",
  pt: "🇧🇷",
  es: "🇪🇸",
};

/**
 * Check if a language is supported
 */
export function isLanguageSupported(language: string): language is Language {
  return SUPPORTED_LANGUAGES.includes(language as Language);
}

/**
 * Check if a language is accepted by the app
 */
export function isLanguageAccepted(language: string): boolean {
  return ACCEPTED_LANGUAGES.includes(language);
}

/**
 * Get language display name
 */
export function getLanguageName(language: Language): string {
  return LANGUAGE_NAMES[language] || language;
}

/**
 * Get language flag
 */
export function getLanguageFlag(language: Language): string {
  return LANGUAGE_FLAGS[language] || "🏳️";
}
